import React from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';

import { cn } from '../../lib/utils';
import type { Project, ProjectStatus } from '../../types/projects';
import { FORMAT_HUE, formatDay, formatOf, hueOf } from './projectFormat';

const STATUS_CLASS: Record<ProjectStatus, string> = {
  deposited: 'border-border text-muted-foreground',
  analysing: 'border-amber-500/60 text-amber-600 dark:text-amber-400 animate-pulse',
  analysed: 'border-sky-500/60 text-sky-600 dark:text-sky-400',
  proposed: 'border-primary/60 bg-primary/10 text-primary',
  reviewing: 'border-primary/60 text-primary',
  qualified: 'border-emerald-500/60 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
};

/** Lifecycle step of a project (Déposé → … → Qualifié). */
export const ProjectStatusPill: React.FC<{ status: ProjectStatus; className?: string }> = ({
  status,
  className,
}) => {
  const { t } = useTranslation();
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center rounded-full border px-2.5 py-0.5 text-xs font-medium',
        STATUS_CLASS[status],
        className
      )}
    >
      {t(`projects.status.${status}`)}
    </span>
  );
};

/** « Diffusé »: the project is visible in the gallery (independent of its lifecycle step). */
export const PublishedBadge: React.FC<{ publishedAt?: string | null; className?: string }> = ({
  publishedAt,
  className,
}) => {
  const { t } = useTranslation();
  if (!publishedAt) return null;
  return (
    <span
      title={t('projects.published.since', { date: formatDay(publishedAt) })}
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded-full border border-foreground bg-foreground px-2.5 py-0.5 text-xs font-medium text-background',
        className
      )}
    >
      <Globe className="h-3 w-3" aria-hidden />
      {t('projects.published.badge')}
    </span>
  );
};

/**
 * A small tag tinted from a hue (format, domain). The hue goes through a CSS
 * variable so the light / dark text shades stay static Tailwind classes.
 */
export const HueTag: React.FC<{ label: string; hue: number | null; title?: string; className?: string }> = ({
  label,
  hue,
  title,
  className,
}) => (
  <span
    title={title}
    style={hue === null ? undefined : ({ '--tag-h': hue } as React.CSSProperties)}
    className={cn(
      'inline-block max-w-[9rem] truncate rounded-full align-middle border px-2 py-0.5 text-[11px] font-medium leading-4',
      hue === null
        ? 'border-border bg-muted text-muted-foreground'
        : 'border-[hsl(var(--tag-h)_65%_50%/0.35)] bg-[hsl(var(--tag-h)_65%_52%/0.13)] text-[hsl(var(--tag-h)_65%_34%)] dark:text-[hsl(var(--tag-h)_70%_74%)]',
      className
    )}
  >
    {label}
  </span>
);

/** At most two words, then « … »: footer chips stay short, the full label goes in the tooltip. */
export function shortLabel(label: string, words = 2): string {
  const parts = label.trim().split(/\s+/);
  return parts.length > words ? `${parts.slice(0, words).join(' ')}…` : label.trim();
}

/** The format + domain tags of a project (card footers). */
export const ProjectTags: React.FC<{ project: Project }> = ({ project: p }) => {
  const { t } = useTranslation();
  const format = formatOf(p);
  const formatLabel = t(`projects.browser.formats.${format}`);
  const domain = p.domain_name?.trim();
  return (
    <span className="flex min-w-0 flex-wrap items-center gap-1.5">
      <HueTag
        label={shortLabel(formatLabel)}
        hue={FORMAT_HUE[format]}
        title={t('projects.card.format', { format: formatLabel })}
      />
      <HueTag
        label={shortLabel(domain || t('projects.noDomain'))}
        hue={domain ? hueOf(domain) : null}
        title={domain || t('projects.noDomain')}
      />
    </span>
  );
};
